import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { LicenseInfo } from '@mui/x-license';
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import posthog from 'posthog-js';
import { RouterProvider } from 'react-router';

import { config } from '#config.ts';
import '#lib/new-relic.ts';
import { ThemeModeProvider } from '#lib/ThemeProvider';
import { queryClient } from './lib/react-query/client.ts';
import { createRouter } from './routes/router.tsx';

import './styles.css';

/**
 * Perfectly fine to be hard-coded.
 * @see https://github.com/mui/mui-x/issues/12308#issuecomment-1976362879
 * @see https://github.com/mui/mui-x/issues/12308#issuecomment-1978327132
 * @see https://mui.com/x/introduction/licensing/#environment-variable-with-next-js
 */
LicenseInfo.setLicenseKey(config.MUI_LICENSE_KEY);

/**
 * No BAA is in place with PostHog, so we limit what it sees to anonymous usage data:
 * - Pageviews/pageleaves stay on (anonymous, route-level usage), but autocapture and session
 *   recording are off because they scrape on-screen text/inputs — the real PHI risk.
 * - `person_profiles: 'identified_only'` keeps usage person-less; identity is attached only at an
 *   explicit opt-in (see the Readiness waitlist).
 * - `before_send` strips all but a safe allowlist of query params from any captured URL, so
 *   free-text like the drilldown `searchText` (which can contain patient names) never reaches
 *   PostHog — `$current_url` is attached to every event, not just pageviews.
 */
const safeUrlQueryParams = new Set(['tab', 'page', 'pageSize', 'sortBy']);
const urlPropertiesToRedact = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
] as const;

function redactUrlQueryParams(url: string): string {
  try {
    const parsed = new URL(url);
    const safeParams = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      if (safeUrlQueryParams.has(key)) {
        safeParams.set(key, value);
      }
    });
    parsed.search = safeParams.toString();
    return parsed.toString();
  } catch {
    return url;
  }
}

posthog.init(config.POSTHOG_TOKEN, {
  api_host: config.POSTHOG_HOST,
  defaults: '2026-01-30',
  autocapture: false,
  disable_session_recording: true,
  person_profiles: 'identified_only',
  before_send: (event) => {
    if (event) {
      for (const property of urlPropertiesToRedact) {
        const value: unknown = event.properties[property];
        if (typeof value === 'string') {
          event.properties[property] = redactUrlQueryParams(value);
        }
      }
    }
    return event;
  },
});

/**
 * Identity is attached only at an explicit opt-in (the Readiness waitlist) and is otherwise sticky
 * across sessions. Reset on every load so a prior opt-in never carries one user's identity into a
 * later session — important on shared devices, since insights has no logout flow to reset on. The
 * EAF enrollment itself is persisted server-side on the person, so this only clears local identity.
 */
posthog.reset();

const router = createRouter(queryClient);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeModeProvider>
            <SnackbarProvider anchorOrigin={{ horizontal: 'center', vertical: 'top' }}>
              <CssBaseline />
              <RouterProvider router={router} />
            </SnackbarProvider>
          </ThemeModeProvider>
        </LocalizationProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>
);
